import { useContext } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ExternalOrderCard } from '@/components/ExternalOrders/ExternalOrderCard';
import { LanguageProviderContext } from '@/providers/LanguageProvider';
import { Tab } from '@/types';

interface ExternalOrdersModalProps {
  isOpen: boolean;
  onClose: () => void;
  pendingOrders: Tab[];
  activeOrders: Tab[];
  pendingCount: number;
  activeCount: number;
}

export const ExternalOrdersModal = ({
  isOpen,
  onClose,
  pendingOrders,
  activeOrders,
  pendingCount,
  activeCount,
}: ExternalOrdersModalProps) => {
  const { translator } = useContext(LanguageProviderContext);

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 animate-overlayShow" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex h-[calc(100dvh-1rem)] w-[calc(100vw-1rem)] max-w-none md:max-w-4xl md:h-auto md:max-h-[calc(100dvh-1rem)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-lg bg-card shadow-lg animate-contentShow">
          <div className="flex shrink-0 items-center justify-between border-b p-6">
            <Dialog.Title className="text-xl font-bold">
              {translator('ExternalOrders.title')}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground"
                aria-label={translator('Global.close')}
              >
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto p-6 pr-4 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-muted-foreground/50">
            <Tabs
              key={`${isOpen}-${pendingCount}-${activeCount}`}
              defaultValue={pendingCount === 0 && activeCount > 0 ? 'proceso' : 'nuevos'}
            >
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="nuevos">
                  {translator('ExternalOrders.new')} ({pendingCount})
                </TabsTrigger>
                <TabsTrigger value="proceso">
                  {translator('ExternalOrders.inProgress')} ({activeCount})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="nuevos" className="mt-4 space-y-4">
                {pendingOrders.length === 0 ? (
                  <p className="py-8 text-center text-muted-foreground">
                    {translator('ExternalOrders.noNewOrders')}
                  </p>
                ) : (
                  pendingOrders.map((order) => (
                    <ExternalOrderCard key={order.id} order={order} type="pending" />
                  ))
                )}
              </TabsContent>

              <TabsContent value="proceso" className="mt-4 space-y-4">
                {activeOrders.length === 0 ? (
                  <p className="py-8 text-center text-muted-foreground">
                    {translator('ExternalOrders.noActiveOrders')}
                  </p>
                ) : (
                  activeOrders.map((order) => (
                    <ExternalOrderCard key={order.id} order={order} type="active" />
                  ))
                )}
              </TabsContent>
            </Tabs>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
